import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/hooks/useTenant";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Upload, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { KpiCards } from "@/components/dashboard/KpiCards";
import { DashboardGrid } from "@/components/dashboard/DashboardGrid";
import { AddInvestmentDialog } from "@/components/portfolio/AddInvestmentDialog";

export default function Dashboard() {
  const navigate = useNavigate();
  const { orgId, familyId, isMFO, isLoading } = useTenant();
  const [addInvestmentOpen, setAddInvestmentOpen] = useState(false);

  // Redirect to onboarding if user has no org
  useEffect(() => {
    if (!isLoading && !orgId) {
      navigate("/onboarding", { replace: true });
    }
  }, [isLoading, orgId, navigate]);

  // Fetch holdings for NAV calculation (same source as Portfolio page)
  const { data: holdingsData } = useQuery({
    queryKey: ['dashboard-holdings', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from('holdings')
        .select('mv')
        .eq('org_id', orgId);

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId
  });

  // Fetch alt vehicles with latest metrics for NAV
  const { data: altVehiclesWithMetrics } = useQuery({
    queryKey: ['dashboard-alts', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from('alt_vehicles')
        .select('id')
        .eq('org_id', orgId);

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data: vehicles, error } = await query;
      if (error) throw error;
      if (!vehicles || vehicles.length === 0) return [];

      // Fetch latest metrics for each vehicle
      const vehicleIds = vehicles.map(v => v.id);
      const { data: metrics } = await supabase
        .from('alt_period_metrics')
        .select('alt_id, computed_nav, period_end')
        .in('alt_id', vehicleIds)
        .order('period_end', { ascending: false });

      // Get latest metric per vehicle
      const latestMetrics = new Map<string, number>();
      metrics?.forEach(m => {
        if (!latestMetrics.has(m.alt_id)) {
          latestMetrics.set(m.alt_id, Number(m.computed_nav) || 0);
        }
      });

      return Array.from(latestMetrics.values());
    },
    enabled: !!orgId
  });

  // Fetch position metrics for return calculations (keep for YTD return)
  const { data: positionMetrics } = useQuery({
    queryKey: ['dashboard-metrics', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return null;

      let query = supabase
        .from('position_metrics')
        .select('irr_itd, distributed_base')
        .eq('org_id', orgId)
        .order('period_end', { ascending: false });

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data, error } = await query.limit(10);
      if (error) throw error;
      return data;
    },
    enabled: !!orgId
  });

  // Fetch YTD fees from transactions
  const { data: feesData } = useQuery({
    queryKey: ['dashboard-fees', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return null;

      const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];

      const query = supabase
        .from('transactions')
        .select('amount, description')
        .eq('class', 'fee')
        .gte('date', yearStart);

      const { data, error } = await query;
      if (error) throw error;

      // Classify fees
      const classifyFee = (desc: string) => {
        const d = (desc || '').toLowerCase();
        if (d.includes('management') || d.includes('mgmt')) return 'mgmt';
        if (d.includes('performance') || d.includes('incentive') || d.includes('carry')) return 'perf';
        if (d.includes('custody') || d.includes('safekeeping')) return 'custody';
        if (d.includes('fx') || d.includes('foreign exchange') || d.includes('currency')) return 'fx';
        return 'other';
      };

      const breakdown = { mgmt: 0, perf: 0, custody: 0, fx: 0, other: 0 };
      let total = 0;

      data?.forEach(tx => {
        const type = classifyFee(tx.description || '');
        const amount = Math.abs(Number(tx.amount) || 0);
        breakdown[type as keyof typeof breakdown] = (breakdown[type as keyof typeof breakdown] || 0) + amount;
        total += amount;
      });

      return { total, breakdown };
    },
    enabled: !!orgId
  });

  // Calculate KPIs from real data
  const kpis = useMemo(() => {
    // Calculate total NAV from holdings + alternatives (same as Portfolio page)
    const holdingsNav = holdingsData?.reduce((sum, h) => sum + (Number(h.mv) || 0), 0) || 0;
    const altsNav = altVehiclesWithMetrics?.reduce((sum, nav) => sum + nav, 0) || 0;
    const totalNav = holdingsNav + altsNav;

    // Get latest period metrics for return calculations
    const latestMetrics = positionMetrics?.[0];

    // Calculate returns (simplified - in production you'd compare periods)
    const ytdReturn = latestMetrics?.irr_itd ? Number(latestMetrics.irr_itd) * 100 : 0;
    const ytdDollarValue = totalNav * (ytdReturn / 100);

    // Get cash from holdings (simplified)
    const cashValue = positionMetrics?.reduce((sum, pm) => {
      // Estimate cash as a portion - in production query holdings where type = 'cash'
      return sum + (Number(pm.distributed_base) || 0);
    }, 0) || 0;

    // Use null when no real data exists (0 is misleading)
    const hasNavData = totalNav > 0;
    const hasYtdData = hasNavData && latestMetrics?.irr_itd !== null && latestMetrics?.irr_itd !== undefined;
    const hasFeesData = (feesData?.total || 0) > 0;
    const hasCashData = cashValue > 0;

    return {
      nav: { value: hasNavData ? totalNav : null, change: null },
      ytdReturn: { value: hasYtdData ? ytdReturn : null, dollarValue: hasYtdData ? ytdDollarValue : null },
      quarterReturn: { value: null, dollarValue: null, quarter: `Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${new Date().getFullYear()}` },
      todayReturn: { value: null, dollarValue: null, change: null },
      feesYTD: {
        value: hasFeesData ? feesData!.total : null,
        breakdown: feesData?.breakdown || { mgmt: 0, perf: 0, custody: 0, fx: 0 }
      },
      cash: { value: hasCashData ? cashValue : null }
    };
  }, [holdingsData, altVehiclesWithMetrics, positionMetrics, feesData]);

  return (
    <div className="flex-1 space-y-6 px-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Your control center for portfolio operations & performance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/copilot')}>
            <MessageSquare className="h-4 w-4" />
            Ask Vintage
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/data/ingestion')}>
            <Upload className="h-4 w-4" />
            Upload Statement
          </Button>
          <Button size="sm" onClick={() => setAddInvestmentOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Investment
          </Button>
          {isMFO && familyId && <Badge variant="secondary">Family Scoped</Badge>}
        </div>
      </div>

      {/* KPI Tiles - Static, not draggable */}
      <KpiCards kpis={kpis} />

      {/* Draggable Widget Grid */}
      <DashboardGrid />

      <AddInvestmentDialog open={addInvestmentOpen} onOpenChange={setAddInvestmentOpen} />
    </div>
  );
}
